import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { InteriorSelectProps } from '@components/v2/InteriorSelect/index';
import useTranslations from '@hooks/useTranslations';
import { useConfiguratorDuck } from '@hooks/useConfiguratorDuck';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import { FEATURES_LIST } from '../../../context/featureSwitchApp';
import { isMarketGB } from '../../../constants/main';
import { useJourneyType } from '@hooks/useJourneyType';
import { useMoneyFormatter } from '@hooks/useMoneyFormatter';
import { IFeature, IPriceV2Types } from '../../../services';
import { useAccessoryMonthlyPrices } from '@hooks/useAccessoryPrices';
import { PRICE_TYPE_EMPLOYEE } from '../../../services/filters/filters';
import { useUILoadingOnConfigurator } from '@hooks/useUILoadingOnConfigurator';
import { useRenderDescription } from '@hooks/useRenderDescription';
import { useDispatch } from 'react-redux';
import { useGTM } from '@hooks/useGTM';
import { useCarDetailsDuck } from '@hooks/useCarDetailsDuck';
import { useUIDuck } from '@hooks/useUIDuck';
import UIDuck from '../../../redux/commonDucks/ui.duck';
import { ImageViewType } from '../../../constants/enums';
import Spinner from '@components/Spinner';
import Tooltip from '@components/Tooltip';
import ConfiguratorDuck from 'src/redux/configurator/configurator.duck';
import Link from '@components/Link';
import { sortByPrice } from '@utils/Color.utils';
import CarGallery from '@components/CarGallery';

export const InteriorSelectTemplate: FC<InteriorSelectProps> = ({ className }) => {
    const { t } = useTranslations();
    const dispatch = useDispatch();
    const contentRef = useRef<HTMLParagraphElement>(null);
    const { getFeatureMonthlyPrice } = useAccessoryMonthlyPrices();
    const { interiorColor, interiorColors } = useConfiguratorDuck();
    const { carDetails } = useCarDetailsDuck();
    const { uiIsLoading } = useUILoadingOnConfigurator();
    const { isLoading } = useUIDuck();
    const { formatConfiguratorPrice } = useMoneyFormatter();
    const { renderDescription } = useRenderDescription();
    const isEmployeePrice = (x: any) => x.type === PRICE_TYPE_EMPLOYEE;
    const { pushUserAction } = useGTM();

    const { isFinance } = useJourneyType();

    const getPrice = (item: IFeature) => {
        const featureMonthlyPrice = getFeatureMonthlyPrice(item);
        const price = item.prices.find(isEmployeePrice);
        const monthlyPrice = featureMonthlyPrice?.price ?? featureMonthlyPrice?.defaultPrice;
        const basePrice = price.basePrice;
        return { monthlyPrice: monthlyPrice ?? 0, basePrice: basePrice ?? 0 };
    };

    const isBudgetChangeAllowed = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_BUDGET_CHANGE_ALLOWED);
    const hideCashPayment = !isBudgetChangeAllowed && isFinance;
    const showExtendedPrice = !isMarketGB || (isMarketGB && !hideCashPayment);

    const [openedInteriorDescriptionId, setOpenedInteriorDescriptionId] = useState(null);
    const [scrollableInteriorDescriptionIds, setScrollableInteriorDescriptionIds] = useState([]);

    useEffect(() => {
        setOpenedInteriorDescriptionId(null);
    }, [carDetails]);

    const onClick = useCallback(
        (interior) => {
            if (!interior.isAvailable) return;
            if (!uiIsLoading) {
                pushUserAction({
                    action: 'Choose::InterriorCladding',
                    eventName: 'uaevent',
                    label: interior.title,
                });
                dispatch(ConfiguratorDuck.setInteriorColor(interior));
                dispatch(UIDuck.toggleImageViewType(ImageViewType.INTERIOR));
            }
        },
        [uiIsLoading, interiorColor, dispatch]
    );

    const images = carDetails?.images?.filter((img: any) => img.type === 'Int') ?? [{}];

    return (
        <div className={className}>
            <span className="sectionTitle" data-testid="TESTING_CONFIG_INTERIOR_SELECT_TITLE">
                {t('configurator.interior.title', {
                    count: interiorColors.length,
                })}
            </span>
            {images?.length > 0 && (
                <CarGallery
                    arrows={false}
                    defaultArrows
                    dots={false}
                    setSmallImage={false}
                    imageSet={ImageViewType.INTERIOR}
                    className="interiorImage"
                    imageItems={images}
                />
            )}
            {isLoading && <Spinner size={36} border={2} color="black" />}
            <div className={`interiors ${isLoading ? 'isLoading' : ''}`}>
                {sortByPrice(interiorColors).map((interior, index) => {
                    const isSelected = interior.id === interiorColor.id;
                    const { monthlyPrice, basePrice } = getPrice(interior);
                    const isShowedDescription = openedInteriorDescriptionId === interior.id;

                    const innerContent = (
                        <div
                            className={`inner ${!interior.isAvailable ? 'disabled' : ''}`}
                            data-testId={`TESTING_INTERIOR_${index}${isSelected ? '_IS_SELECTED' : ''}`}
                        >
                            <div>
                                <span className="interiorTitle" data-testId={`TESTING_INTERIOR_NAME_${index}`}>
                                    <span className={`icon ${isSelected ? 'selectedBorder' : ''}`}>
                                        {isSelected && <span className="selected" />}
                                    </span>
                                    <span>{interior.title}</span>
                                </span>
                                <span className="interiorPrice" data-testId={`TESTING_INTERIOR_PRICE_${index}`}>
                                    {showExtendedPrice &&
                                        basePrice > 0 &&
                                        formatConfiguratorPrice(
                                            basePrice,
                                            '+ ',
                                            true,
                                            '',
                                            null,
                                            monthlyPrice ? t('configurator.optionPrice.suffix') : ''
                                        )}

                                    {monthlyPrice > 0 && (
                                        <>
                                            {formatConfiguratorPrice(monthlyPrice, '+ ', true, '', !isFinance)}
                                            <span className="priceSuffix">
                                                {' '}
                                                {t('car.price.monthlyPriceLabel').toUpperCase()}
                                            </span>
                                        </>
                                    )}
                                    {basePrice === 0 && monthlyPrice === 0 && t('configurator.colors.includedInPrice')}
                                </span>
                            </div>
                            {interior?.description && (
                                <p
                                    ref={contentRef}
                                    className={`interiorDescription ${
                                        openedInteriorDescriptionId === interior.id ? 'withoutReduce' : ''
                                    }`}
                                >
                                    {renderDescription(interior?.description)}
                                </p>
                            )}
                            {interior?.description &&
                                (((contentRef?.current?.childNodes?.[0] as HTMLElement)?.scrollHeight || contentRef?.current?.scrollHeight) > contentRef?.current?.clientHeight + 5 ||
                                    scrollableInteriorDescriptionIds.includes(interior.id)) && (
                                    <span
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setScrollableInteriorDescriptionIds([
                                                ...scrollableInteriorDescriptionIds,
                                                interior.id,
                                            ]);
                                            setOpenedInteriorDescriptionId(isShowedDescription ? null : interior.id);
                                        }}
                                        className="interiorDescriptionLink"
                                    >
                                        {isShowedDescription
                                            ? t('configurator.reduce')
                                            : t('option.detail.shortText.info')}
                                    </span>
                                )}
                        </div>
                    );

                    return (
                        <div key={index}>
                            <button
                                onClick={() => onClick(interior)}
                                className={`interior ${isSelected ? 'isSelected' : ''}`}
                            >
                                {!interior.isAvailable ? (
                                    <Tooltip place="bottom" effect="solid" handle={innerContent}>
                                        {t('configurator.incompatibleInterior')}
                                    </Tooltip>
                                ) : (
                                    innerContent
                                )}
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
